import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { shippingAccessScope } from '../../hooks/useShippingRequest';
import useAccessibleDialog from '../../hooks/useAccessibleDialog';
import { useLocale, useT } from '../../i18n/LanguageContext';
import { moneyMinor } from '../../utils/money';
import { displayFamilyName } from './distributorBankSummary';
import {
  readPurchaseReceipt,
  purchaseState,
  purchaseCreditMessage,
  purchaseBankStateKey,
} from './distributorPurchaseReceipt';

function PurchaseDetailDialog({ detail, locale, onClose, t }) {
  const { dialogRef, titleId } = useAccessibleDialog({ open: true, onClose });
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="me-dw-card flex max-h-[90dvh] w-full max-w-xl flex-col overflow-hidden"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-[#e1ebeb] px-5 py-4">
          <h2 id={titleId} className="min-w-0 text-lg font-bold text-[#153d48]">
            {t(detail.status === 'credited' ? 'Purchase receipt detail' : 'Purchase draft detail')}
          </h2>
          <button type="button" className="me-dw-link min-h-[44px] shrink-0 px-2" onClick={onClose}>
            {t('Close')}
          </button>
        </header>
        <div className="min-h-0 space-y-2 overflow-y-auto break-words p-5 text-sm">
          <p>
            {t(detail.status === 'credited' ? 'Purchase ID' : 'Draft ID')}: {detail._id}
          </p>
          <p>
            {t('Product family')}: {displayFamilyName(detail.familyKey)}
          </p>
          <p>
            {detail.units.toLocaleString(locale)} {t('units')} · {moneyMinor(detail.amountCents)}{' '}
            USD
          </p>
          <p>
            {t({ wire: 'Wire', ach: 'ACH', card: 'Credit card' }[detail.method])} ·{' '}
            {t(purchaseState(detail))}
          </p>
          <p>{t(purchaseCreditMessage(detail))}</p>
          {detail.status === 'credited' && (
            <p>
              {t('Bank entry ID')}: {detail.bankEntryId} ·{' '}
              {new Date(detail.creditedAt).toLocaleString(locale)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DistributorPurchaseHistory({ revision = 0, onCredited, onBankChanged }) {
  const t = useT();
  const locale = useLocale();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [opening, setOpening] = useState(false);
  const session = useRef(captureSession());
  const scope = useRef(shippingAccessScope());
  const mounted = useRef(false);
  const sequence = useRef(0);
  const detailSequence = useRef(0);
  const detailTrigger = useRef(null);
  const notifiedCredits = useRef(new Map());
  const explicitRefresh = useRef(false);
  const bankCallback = useRef(onBankChanged);
  bankCallback.current = onBankChanged;
  const creditCallback = useRef(onBankChanged || onCredited);
  creditCallback.current = onBankChanged || onCredited;
  const observeCredit = (row) => {
    const key = purchaseBankStateKey(row);
    if (
      (row.status === 'credited' ||
        (row.achAttempt || row.cardAttempt)?.state === 'returned' ||
        row.returnReview) &&
      notifiedCredits.current.get(row._id) !== key
    ) {
      notifiedCredits.current.set(row._id, key);
      return true;
    }
  };
  const current = () =>
    mounted.current && isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      sequence.current += 1;
      detailSequence.current += 1;
    };
  }, []);
  useEffect(() => {
    if (!current()) return;
    const generation = ++sequence.current;
    detailSequence.current += 1;
    setRows(null);
    setDetail(null);
    setOpening(false);
    setError('');
    const read = async () => {
      try {
        const response = await distributorService.listPurchases(page);
        if (!current() || generation !== sequence.current) return;
        const value = response?.data?.data;
        if (
          !Array.isArray(value?.data) ||
          !Number.isSafeInteger(value.total) ||
          value.total < 0 ||
          value.page !== page ||
          value.limit !== 20 ||
          value.data.length > 20
        )
          throw new Error('Invalid purchase history');
        const validated = value.data.map((row) => readPurchaseReceipt({ data: { data: row } }));
        if (page > 1 && page > Math.max(1, Math.ceil(value.total / 20))) {
          setPage(Math.max(1, Math.ceil(value.total / 20)));
          return;
        }
        setRows(validated);
        setTotal(value.total);
        const changed = validated.map(observeCredit).some(Boolean);
        if (changed) creditCallback.current?.();
        else if (explicitRefresh.current) bankCallback.current?.();
        explicitRefresh.current = false;
      } catch {
        if (current() && generation === sequence.current)
          setError('Purchase draft history is unavailable. Please retry.');
      }
    };
    read();
  }, [page, revision, retry]);
  const open = async (id, trigger) => {
    if (!current() || opening) return;
    // Native browsers can blur View when loading disables it before the dialog mounts.
    detailTrigger.current = trigger;
    const generation = ++detailSequence.current;
    setDetail(null);
    setOpening(true);
    setError('');
    try {
      const value = readPurchaseReceipt(await distributorService.getPurchase(id));
      if (!current() || generation !== detailSequence.current) return;
      if (value._id !== id) throw new Error('Invalid purchase draft');
      setDetail(value);
      if (observeCredit(value)) creditCallback.current?.();
    } catch {
      if (current() && generation === detailSequence.current)
        setError('This purchase draft could not be opened. Please retry.');
    } finally {
      if (current() && generation === detailSequence.current) setOpening(false);
    }
  };
  const closeDetail = () => {
    const trigger = detailTrigger.current;
    setDetail(null);
    requestAnimationFrame(() => {
      const focused = document.activeElement;
      if (
        current() &&
        trigger?.isConnected &&
        !trigger.disabled &&
        !trigger.closest('[hidden], [inert], [aria-hidden="true"]') &&
        (!focused || focused === document.body || !focused.isConnected)
      )
        trigger.focus();
    });
  };
  const move = (nextPage) => {
    if (!current()) return;
    sequence.current += 1;
    detailSequence.current += 1;
    setRows(null);
    setDetail(null);
    setPage(nextPage);
  };
  return (
    <section aria-label={t('Purchase records')} className="me-dw-card me-dw-bank-records-card">
      <div className="me-dw-bank-section-head me-dw-bank-card-heading">
        <h2 className="me-dw-bank-section-title">{t('Purchase records')}</h2>
        <button
          type="button"
          className="me-dw-link"
          onClick={() => {
            if (current()) {
              explicitRefresh.current = true;
              setRetry((value) => value + 1);
            }
          }}
        >
          {t('Refresh purchases')}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-red-700">
          {t(error)}{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              if (current()) setRetry((value) => value + 1);
            }}
          >
            {t('Retry')}
          </button>
        </p>
      )}
      {!rows && !error && <p role="status">{t('Loading purchase drafts...')}</p>}
      {rows?.length === 0 && <p className="me-dw-bank-empty">{t('No purchase records yet.')}</p>}
      {rows?.length > 0 && (
        <ul className="me-dw-list overflow-hidden rounded-xl border border-[#e1ebeb]">
          {rows.map((row) => (
            <li
              key={row._id}
              className="flex flex-wrap items-center justify-between gap-3 p-3 text-sm"
            >
              <span>
                <strong>{displayFamilyName(row.familyKey)}</strong> ·{' '}
                {row.units.toLocaleString(locale)} {t('units')}
                <span className="block text-slate-600">
                  {new Date(row.createdAt).toLocaleString(locale)} · {t(purchaseState(row))}
                </span>
              </span>
              <span className="flex items-center gap-3">
                <strong>{moneyMinor(row.amountCents)} USD</strong>
                <button
                  type="button"
                  className="me-dw-link"
                  disabled={opening}
                  onClick={(event) => open(row._id, event.currentTarget)}
                  aria-label={t(
                    row.status === 'credited'
                      ? 'View purchase receipt {id}'
                      : 'View purchase draft {id}',
                    { id: row._id }
                  )}
                >
                  {t('View')}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {total > 20 && (
        <nav aria-label={t('Purchase draft pages')} className="flex items-center gap-3">
          <button type="button" disabled={!rows || page <= 1} onClick={() => move(page - 1)}>
            {t('Previous')}
          </button>
          <span>
            {t('Page {page} of {pages}', { page, pages: Math.max(1, Math.ceil(total / 20)) })}
          </span>
          <button
            type="button"
            disabled={!rows || page >= Math.ceil(total / 20)}
            onClick={() => move(page + 1)}
          >
            {t('Next')}
          </button>
        </nav>
      )}
      {opening && <p role="status">{t('Opening purchase draft...')}</p>}
      {detail && (
        <PurchaseDetailDialog
          key={detail._id}
          detail={detail}
          locale={locale}
          onClose={closeDetail}
          t={t}
        />
      )}
    </section>
  );
}
